import { getJson, pathSegment, postFile, postJson, queryString } from "../client";
import { RESUME_BASE } from "./base";
import type { Resume } from "./resume";

/** A file a resume is imported from, and the model's read of it. */

/**
 * A person's name, how to reach them, where they are and their links, as
 * an imported file gives them: guessed in code on upload, confirmed by the
 * person before the read. `resume_api::resume::ImportDetails`.
 */
export interface ImportDetails {
    /** Empty in a guess when the page gives none. */
    name: string;
    email?: string | null;
    phone?: string | null;
    location?: string | null;
    /** Ten at most. */
    links: string[];
}

/** What kind of file an upload is, told from its bytes. */
export type UploadKind = "PDF" | "DOCX" | "MARKDOWN" | "TEXT";

/** A file a person imports their resume from, as kept. `resume_api::responses::Upload`. */
export interface Upload {
    upload_id: string;
    kind: UploadKind;
    filename?: string | null;
    byte_size: number;
    sha256: string;
    /** How many pages of a PDF were read, five at most. Absent for anything else. */
    pages?: number | null;
    /** How many characters of text the file holds. */
    characters: number;
    uploaded_at: string;
    /** The person's details as the text gives them, for the person to confirm. */
    details: ImportDetails;
}

/** A file sent too soon after the last ones read. `resume_api::responses::TryAgainLater`. */
export interface TryAgainLater {
    message: string;
    retry_after_seconds: number;
    retry_at: string;
}

/** Why a field of a read is worth a look. `resume_api::responses::IssueKind`. */
export type IssueKind = "NOT_ON_PAGE" | "MISSING" | "UNSURE" | "NOT_CONFIRMED";

/** One thing the reader was not sure of. */
export interface ReadIssue {
    /** `name`, `phone`, `skills[2]`, `experience[0].end`, `experience[1].bullets[3]`, `education[0].year`, `certifications[0].issuer`. */
    field: string;
    kind: IssueKind;
    note: string;
}

/** A file read into a resume's fields: a draft to check. `resume_api::responses::ResumeRead`. */
export interface ResumeRead {
    upload_id: string;
    draft: Resume;
    /** In the order the fields come in the resume. */
    issues: ReadIssue[];
    /** The model that read it. */
    model: string;
    read_at: string;
}

/**
 * Sends a file to import from: PDF, Word, Markdown or plain text, 10 MB
 * at most. 429 with {@link TryAgainLater} when two were read in the last
 * five minutes.
 */
export function uploadResumeFile(file: Blob, filename: string | null): Promise<Upload> {
    return postFile(`${RESUME_BASE}/uploads${queryString({ filename })}`, file);
}

/** Has an upload read into a draft, with the details as the person confirms them. Spends one import. */
export async function readUpload(uploadId: string, details: ImportDetails): Promise<ResumeRead> {
    return postJson(`${RESUME_BASE}/uploads/${pathSegment(uploadId)}/read`, { details });
}

/** What an upload was read as. 404 until it has been read. */
export async function getUploadRead(uploadId: string): Promise<ResumeRead> {
    return getJson(`${RESUME_BASE}/uploads/${pathSegment(uploadId)}/read`);
}
